// src/pages/WorkspaceLog.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./css/log.css";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { API_BASE } from "../services/server";
import useKoncernMembers from "../hooks/useKoncernMembers";
import useKoncernScope from "../hooks/useKoncernScope";
import KoncernScopePicker from "./components/KoncernScopePicker";

/**
 * Ändringslogg – visar audit events för ett workspace.
 *
 * API (UPDATED):
 *  GET /loggs/audit-events/?orgnr=<orgnr>
 *  GET /loggs/audit-events/<eventId>/?orgnr=<orgnr>
 */

export default function WorkspaceLog({ apiBase = "" }) {
  const params = useParams();

  const orgnr = String(
    params.orgnr || params.company || params.workspaceId || params.workspace || ""
  ).trim();

  const navKey = orgnr || "logg";
  const BASE = apiBase || API_BASE;

  // Koncern scope: parent-only (default), specific subsidiary, or whole group.
  const { koncernInfo, isKoncern } = useKoncernMembers(orgnr);
  const { scope, setScope, activeOrgnr, isAllMode } = useKoncernScope(orgnr);
  const fetchOrgnr = activeOrgnr || orgnr;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [query, setQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");

  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  // ---- Fetch listan (by orgnr) ----
  useEffect(() => {
    if (!orgnr) {
      setLoading(false);
      setEvents([]);
      setErr("Saknar orgnr i URL.");
      return;
    }

    // Cache per resolved scope so switching companies doesn't show stale
    // rows from the previous company.
    const scopeKey = isAllMode
      ? `koncern:${orgnr}`
      : `single:${fetchOrgnr}`;
    const cacheKey = `orgnr:${scopeKey}:audit_events`;
    const ctrl = new AbortController();

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setEvents(parsed);
          setLoading(false);
        }
      } catch {
        // ignore
      }
    }

    (async () => {
      setLoading(true);
      setErr("");
      try {
        // All-mode: pass comma-separated list of parent + every subsidiary.
        // Single-company mode: standard ?orgnr=... param.
        let url;
        if (isAllMode && koncernInfo?.all?.length) {
          const orgnrs = koncernInfo.all.map((c) => c.orgnr).join(",");
          url = `${API_BASE}/loggs/audit-events/?orgnrs=${encodeURIComponent(orgnrs)}`;
        } else {
          url = `${API_BASE}/loggs/audit-events/?orgnr=${encodeURIComponent(fetchOrgnr)}`;
        }

        const res = await fetch(url, {
          signal: ctrl.signal,
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (res.status === 403) throw new Error("403: Du saknar behörighet att se loggar.");
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || `HTTP ${res.status}`);
        }

        const data = await res.json();
        const items = Array.isArray(data) ? data : data.results || [];

        setEvents(items);
        sessionStorage.setItem(cacheKey, JSON.stringify(items));
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error(e);
        setErr(e?.message || "Kunde inte hämta loggar.");
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [orgnr, fetchOrgnr, isAllMode, koncernInfo, BASE]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return (events || [])
      .filter((e) => (actionFilter === "all" ? true : e.action === actionFilter))
      .filter((e) => {
        if (!q) return true;
        const actor = formatActor(e.actor_user);
        const hay = [
          actor.name,
          actor.sub,
          e.model_label,
          e.object_id,
          e.action,
          e.request_id || "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
  }, [events, query, actionFilter]);

  async function loadHistory(row) {
    if (!row?.model_label || !row?.object_id) return;
    setHistoryLoading(true);
    setHistoryErr("");
    setHistory(null);
    setShowHistory(true);
    try {
      const url = `${API_BASE}/loggs/object-history/?orgnr=${encodeURIComponent(orgnr)}&model=${encodeURIComponent(row.model_label)}&object_id=${encodeURIComponent(row.object_id)}`;
      const res = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHistory(data);
    } catch (e) {
      setHistoryErr(e?.message || "Kunde inte hämta historik.");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function openDetail(row) {
    if (!row?.id) return;

    setSelected(row);
    setSelectedDetail(null);
    setDetailErr("");
    setShowHistory(false);
    setHistory(null);
    setDetailLoading(true);

    try {
      const url = `${API_BASE}/loggs/audit-events/${row.id}/?orgnr=${encodeURIComponent(orgnr)}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (res.status === 403) throw new Error("403: Du saknar behörighet att se detaljen.");
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setSelectedDetail(data);
    } catch (e) {
      console.error(e);
      setDetailErr(e?.message || "Kunde inte hämta detalj.");
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <Main navItems={makeCompanyNav(navKey)}>
      <div className="workspace-log-root">
        <header className="workspace-log-header">
          <div>
            <h1 className="workspace-log-title">Ändringslogg</h1>
            <p className="workspace-log-subtitle">
              Visar alla ändringar i workspacet. Om en ändring gjorts automatiskt
              visas <strong>System</strong>.
            </p>

            <div className="workspace-log-meta">
              {orgnr ? (
                <span className="workspace-log-pill">
                  {isAllMode ? "Hela koncernen" : `Org.nr: ${fetchOrgnr}`}
                </span>
              ) : null}
              <span className="workspace-log-pill">Poster: {filtered.length}</span>
            </div>
          </div>

          <div className="workspace-log-controls">
            {isKoncern && (
              <KoncernScopePicker koncernInfo={koncernInfo} scope={scope} setScope={setScope} />
            )}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sök på användare, objekt, request-id…"
              className="workspace-log-input"
            />

            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="workspace-log-select"
            >
              <option value="all">Alla</option>
              <option value="create">Skapade</option>
              <option value="update">Ändrade</option>
              <option value="delete">Raderade</option>
            </select>
          </div>
        </header>

        {loading && (
          <div className="workspace-log-state">
            <div className="workspace-log-spinner" />
            <span className="workspace-log-muted">Laddar loggar…</span>
          </div>
        )}

        {err && !loading && (
          <div className="workspace-log-state workspace-log-state--error" role="alert">
            {err}
          </div>
        )}

        {!loading && !err && (
          <section className="workspace-log-card">
            <div className="workspace-log-card-header">
              <h2>Senaste händelser</h2>
              <span className="workspace-log-muted">{filtered.length} poster</span>
            </div>

            {filtered.length === 0 ? (
              <div className="workspace-log-empty">Inga loggar hittades.</div>
            ) : (
              <div className="workspace-log-table-wrap" style={{ overflowX: "auto" }}>
                <table
                  className="workspace-log-table"
                  style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={LOG_TH}>Tid</th>
                      <th style={LOG_TH}>Vem</th>
                      <th style={LOG_TH}>Händelse</th>
                      <th style={LOG_TH}>Objekt</th>
                      <th style={LOG_TH}>Ändrat</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.map((row) => {
                      const actor = formatActor(row.actor_user);
                      const a = formatAction(row.action);
                      const changedKeys = summarizeDiff(row.diff);
                      const modelNice = formatModelLabel(row.model_label);

                      return (
                        <tr
                          key={row.id}
                          className="workspace-log-row"
                          onClick={() => openDetail(row)}
                          title="Klicka för mer info"
                          style={{ cursor: "pointer", transition: "background 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                        >
                          <td style={LOG_TD_TIME}>{formatTime(row.created_at)}</td>

                          <td style={LOG_TD_WHO}>
                            <div style={LOG_TWO_LINE}>
                              <div style={LOG_ELLIPSIS} title={actor.name}>{actor.name}</div>
                              {actor.sub ? (
                                <div style={LOG_ELLIPSIS_SUB} title={actor.sub}>{actor.sub}</div>
                              ) : null}
                            </div>
                          </td>

                          <td style={LOG_TD_ACTION}>
                            <Badge tone={a.tone}>{a.label}</Badge>
                          </td>

                          <td style={LOG_TD_OBJECT}>
                            <div style={LOG_TWO_LINE}>
                              <div style={LOG_ELLIPSIS} title={modelNice}>{modelNice}</div>
                              <div style={LOG_ELLIPSIS_SUB} title={`${row.model_label} · #${row.object_id}`}>
                                {row.model_label} · #{row.object_id}
                              </div>
                            </div>
                          </td>

                          <td style={LOG_TD_DIFF}>
                            {row.action === "update" ? (
                              changedKeys.length ? (
                                <div style={LOG_CHIPS}>
                                  {changedKeys.map((k) => (
                                    <span key={k} style={LOG_CHIP} title={k}>
                                      {k.length > 20 ? k.slice(0, 20) + "…" : k}
                                    </span>
                                  ))}
                                  {Object.keys(row.diff || {}).length > changedKeys.length ? (
                                    <span style={{ color: "#64748b", fontSize: 12 }}>
                                      +{Object.keys(row.diff || {}).length - changedKeys.length} till
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <span style={{ color: "#64748b" }}>Detaljer i posten</span>
                              )
                            ) : row.action === "create" ? (
                              <span style={{ color: "#64748b" }}>Ny post skapad</span>
                            ) : (
                              <span style={{ color: "#64748b" }}>Post raderad</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        <Drawer
          open={!!selected}
          onClose={() => {
            setSelected(null);
            setSelectedDetail(null);
            setDetailErr("");
            setShowHistory(false);
            setHistory(null);
          }}
          title={
            selected
              ? `${formatAction(selected.action).label} ${formatModelLabel(
                  selected.model_label
                )} #${selected.object_id}`
              : ""
          }
        >
          {/* Tab bar */}
          {selected && (
            <div className="workspace-log-tabs">
              <button
                className={`workspace-log-tab${!showHistory ? " workspace-log-tab--active" : ""}`}
                onClick={() => setShowHistory(false)}
              >
                Händelse
              </button>
              <button
                className={`workspace-log-tab${showHistory ? " workspace-log-tab--active" : ""}`}
                onClick={() => !history && !historyLoading ? loadHistory(selected) : setShowHistory(true)}
              >
                Versionshistorik
              </button>
            </div>
          )}

          {!showHistory ? (
            detailLoading ? (
              <div className="workspace-log-muted">Laddar detalj…</div>
            ) : detailErr ? (
              <div className="workspace-log-state workspace-log-state--error" role="alert">
                {detailErr}
              </div>
            ) : selectedDetail ? (
              <div className="workspace-log-detail">
                <div className="workspace-log-detail-grid">
                  <InfoBox label="Tid" value={formatTime(selectedDetail.created_at)} />
                  <InfoBox
                    label="Vem"
                    value={formatActor(selectedDetail.actor_user).name}
                    sub={formatActor(selectedDetail.actor_user).sub}
                  />
                  <InfoBox
                    label="Request"
                    value={selectedDetail.request_id || "—"}
                    sub={selectedDetail.ip ? `IP: ${selectedDetail.ip}` : ""}
                  />
                </div>

                <div>
                  <div className="workspace-log-detail-title">Ändring</div>
                  <DiffTable diff={selectedDetail.diff} />
                </div>

                <details className="workspace-log-raw">
                  <summary>Visa rådata (före/efter)</summary>
                  <div className="workspace-log-raw-grid">
                    <div>
                      <div className="workspace-log-raw-label">Före</div>
                      <pre className="workspace-log-pre">
                        {selectedDetail.before ? JSON.stringify(selectedDetail.before, null, 2) : "—"}
                      </pre>
                    </div>
                    <div>
                      <div className="workspace-log-raw-label">Efter</div>
                      <pre className="workspace-log-pre">
                        {selectedDetail.after ? JSON.stringify(selectedDetail.after, null, 2) : "—"}
                      </pre>
                    </div>
                  </div>
                </details>
              </div>
            ) : (
              <div className="workspace-log-muted">Ingen detalj tillgänglig.</div>
            )
          ) : (
            <VersionTimeline
              history={history}
              loading={historyLoading}
              error={historyErr}
            />
          )}
        </Drawer>
      </div>
    </Main>
  );
}

/* ─────────────────────────────────────────────────────────
   Inline styles for table truncation
───────────────────────────────────────────────────────── */

const LOG_CELL_BASE = {
  padding: "12px 14px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "top",
  fontSize: 13,
};

const LOG_TH = {
  ...LOG_CELL_BASE,
  textAlign: "left",
  fontWeight: 600,
  color: "#64748b",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.3,
};

const LOG_ELLIPSIS = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: 13,
  color: "#1c1917",
  fontWeight: 500,
};

const LOG_ELLIPSIS_SUB = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: 12,
  color: "#78716c",
};

const LOG_TWO_LINE = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  maxWidth: "100%",
};

const LOG_TD_TIME = {
  ...LOG_CELL_BASE,
  whiteSpace: "nowrap",
  width: 140,
  minWidth: 140,
  color: "#475569",
};

const LOG_TD_WHO = {
  ...LOG_CELL_BASE,
  width: "22%",
  maxWidth: 0,       // tricks <td> into respecting width for ellipsis children
  overflow: "hidden",
};

const LOG_TD_ACTION = {
  ...LOG_CELL_BASE,
  width: 110,
  minWidth: 110,
  whiteSpace: "nowrap",
};

const LOG_TD_OBJECT = {
  ...LOG_CELL_BASE,
  width: "24%",
  maxWidth: 0,
  overflow: "hidden",
};

const LOG_TD_DIFF = {
  ...LOG_CELL_BASE,
  maxWidth: 0,
  overflow: "hidden",
};

const LOG_CHIPS = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  alignItems: "center",
};

const LOG_CHIP = {
  display: "inline-block",
  padding: "2px 8px",
  background: "#f1f5f9",
  color: "#334155",
  fontSize: 12,
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  maxWidth: 180,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/* ======================= Small helpers (unchanged) ======================= */

function InfoBox({ label, value, sub }) {
  return (
    <div className="workspace-log-infobox">
      <div className="workspace-log-infobox-label">{label}</div>
      <div className="workspace-log-infobox-value">{value}</div>
      {sub ? <div className="workspace-log-infobox-sub">{sub}</div> : null}
    </div>
  );
}

function formatActor(actorUser) {
  if (!actorUser) return { name: "System", sub: "Automatiskt" };
  const fullName = [actorUser.first_name, actorUser.last_name].filter(Boolean).join(" ").trim();

  const primary = fullName || actorUser.username || actorUser.email || "Okänd användare";
  const secondary =
    actorUser.email && actorUser.email !== primary ? actorUser.email : actorUser.username || "";

  return { name: primary, sub: secondary };
}

function formatAction(action) {
  switch (action) {
    case "create":
      return { label: "Skapade", tone: "success" };
    case "update":
      return { label: "Ändrade", tone: "info" };
    case "delete":
      return { label: "Raderade", tone: "danger" };
    default:
      return { label: action || "Händelse", tone: "neutral" };
  }
}

function formatModelLabel(modelLabel) {
  if (!modelLabel) return "Okänt objekt";
  const parts = modelLabel.split(".");
  const last = parts[parts.length - 1] || modelLabel;
  return last.charAt(0).toUpperCase() + last.slice(1);
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarizeDiff(diff) {
  if (!diff || typeof diff !== "object") return [];
  return Object.keys(diff).slice(0, 4);
}

function toDisplayValue(v) {
  if (v === null) return "null";
  if (v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v.length > 160 ? v.slice(0, 160) + "…" : v;

  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function Badge({ tone = "neutral", children }) {
  const cls =
    tone === "success"
      ? "workspace-log-badge workspace-log-badge--success"
      : tone === "info"
      ? "workspace-log-badge workspace-log-badge--info"
      : tone === "danger"
      ? "workspace-log-badge workspace-log-badge--danger"
      : "workspace-log-badge";

  return <span className={cls}>{children}</span>;
}

function Drawer({ open, onClose, title, children }) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1000,
          animation: "loggDrawerFade 0.2s ease-out",
        }}
      />
      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(720px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          animation: "loggDrawerSlide 0.25s ease-out",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #e7e5e4",
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: "#78716c", marginBottom: 4, letterSpacing: 0.5 }}>
              ÄNDRINGSLOGG
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "#1c1917",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={title}
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng"
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              color: "#78716c",
              cursor: "pointer",
              padding: "4px 8px",
              marginLeft: 12,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {children}
        </div>
      </aside>
      <style>{`
        @keyframes loggDrawerFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes loggDrawerSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}

function VersionTimeline({ history, loading, error }) {
  const [expanded, setExpanded] = useState(null);

  if (loading) return <div className="workspace-log-muted">Laddar historik…</div>;
  if (error) return <div className="workspace-log-state workspace-log-state--error">{error}</div>;
  if (!history) return <div className="workspace-log-muted">Klicka på fliken för att ladda historik.</div>;
  if (history.count === 0) return <div className="workspace-log-muted">Ingen historik hittades.</div>;

  const versions = [...history.versions].reverse(); // newest first

  return (
    <div className="workspace-log-timeline">
      {versions.map((v) => {
        const actor = formatActor(v.actor_user);
        const isOpen = expanded === v.version;
        const isLatest = v.version === history.count;
        return (
          <div key={v.id} className={`workspace-log-tl-item${isLatest ? " workspace-log-tl-item--latest" : ""}`}>
            <div className="workspace-log-tl-line" />
            <div className="workspace-log-tl-dot" />
            <div className="workspace-log-tl-body">
              <div className="workspace-log-tl-header" onClick={() => setExpanded(isOpen ? null : v.version)}>
                <div className="workspace-log-tl-meta">
                  <span className="workspace-log-tl-version">v{v.version}</span>
                  {isLatest && <span className="workspace-log-tl-latest-badge">Senaste</span>}
                  <Badge tone={formatAction(v.action).tone}>{formatAction(v.action).label}</Badge>
                </div>
                <div className="workspace-log-tl-info">
                  <span>{actor.name}</span>
                  <span className="workspace-log-muted">{formatTime(v.created_at)}</span>
                </div>
                <button className="workspace-log-tl-toggle">{isOpen ? "▲" : "▼"}</button>
              </div>

              {isOpen && (
                <div className="workspace-log-tl-detail">
                  {v.action === "update" && <DiffTable diff={v.diff} />}
                  {v.action === "create" && (
                    <details className="workspace-log-raw">
                      <summary>Visa snapshot</summary>
                      <pre className="workspace-log-pre">{JSON.stringify(v.after, null, 2)}</pre>
                    </details>
                  )}
                  {v.action === "delete" && (
                    <details className="workspace-log-raw">
                      <summary>Visa snapshot</summary>
                      <pre className="workspace-log-pre">{JSON.stringify(v.before, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DiffTable({ diff }) {
  if (!diff || typeof diff !== "object" || Object.keys(diff).length === 0) {
    return <div className="workspace-log-muted">Ingen diff tillgänglig.</div>;
  }

  const rows = Object.entries(diff);

  return (
    <div className="workspace-log-diff">
      <table className="workspace-log-diff-table">
        <thead>
          <tr>
            <th>Fält</th>
            <th>Före</th>
            <th>Efter</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, change]) => (
            <tr key={key}>
              <td className="workspace-log-diff-key">{key}</td>
              <td>
                <pre className="workspace-log-pre workspace-log-pre--small">
                  {toDisplayValue(change?.from)}
                </pre>
              </td>
              <td>
                <pre className="workspace-log-pre workspace-log-pre--small">
                  {toDisplayValue(change?.to)}
                </pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


